import { Message } from '../models/copilot.models';

export interface ChatSearchMatch {
  globalIndex: number;
  messageId: string;
  start: number;
  end: number;
}

export interface ChatSearchMatchSlice {
  globalIndex: number;
  start: number;
  end: number;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Case-insensitive, non-overlapping matches in message order. */
export function computeChatSearchMatches(
  messages: readonly Message[],
  query: string
): ChatSearchMatch[] {
  const needle = query.trim();
  if (!needle) return [];

  const lowerNeedle = needle.toLowerCase();
  let globalIndex = 0;
  const out: ChatSearchMatch[] = [];

  for (const msg of messages) {
    const text = msg.content ?? '';
    const lower = text.toLowerCase();
    let start = 0;
    while (true) {
      const i = lower.indexOf(lowerNeedle, start);
      if (i === -1) break;
      out.push({
        globalIndex: globalIndex++,
        messageId: msg.id,
        start: i,
        end: i + needle.length,
      });
      start = i + needle.length;
    }
  }

  return out;
}

export function normalizeActiveMatchIndex(
  activeMatchIndex: number,
  matchCount: number
): number {
  if (matchCount <= 0) return 0;
  return ((activeMatchIndex % matchCount) + matchCount) % matchCount;
}

/**
 * Wraps match ranges in <mark> for highlighting (active hit uses
 * `chat-search-mark-active`). Note: ngx-markdown sanitization usually strips
 * `id` on `<mark>`; scrolling targets `.chat-search-mark-active`. Use a single
 * hyphen for the modifier class so sanitizer/CSS reliably keep it.
 * Ranges must belong to the same `content` string; indices are non-overlapping.
 */
export function injectMarkdownSearchMarks(
  content: string,
  ranges: readonly ChatSearchMatchSlice[],
  activeGlobalIndex: number
): string {
  if (!ranges.length) return content;

  const sorted = [...ranges].sort((a, b) => b.start - a.start);
  let result = content;

  for (const r of sorted) {
    const inner = result.slice(r.start, r.end);
    const isActive = r.globalIndex === activeGlobalIndex;
    const hitClass = `chat-search-hit-${r.globalIndex}`;
    const cls = isActive
      ? `chat-search-mark chat-search-mark-active ${hitClass}`
      : `chat-search-mark ${hitClass}`;
    const wrapped = `<mark class="${cls}">${escapeHtml(inner)}</mark>`;
    result = result.slice(0, r.start) + wrapped + result.slice(r.end);
  }

  return result;
}
